// COMPONENT IMPORTS
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";

// COMPONENT IMPORTS
import "./CustomTableStyles.css";
import DataTable from "react-data-table-component";
import Button from "components/form/Button";
import Skeleton from "react-loading-skeleton";
import { NoRecordFoundIcon } from "icons/action/NoRecordFound";
import { PaymentColumns } from "types/payments";
import { ScheduledColumns } from "types/scheduled";
import { SubscriptionAndPlansColumn } from "types/subscriptionAndPlans";
import { ActionCenterColumn } from "types/actionCenter";

interface CustomTableProps {
  columns:
    | PaymentColumns[]
    | ScheduledColumns[]
    | SubscriptionAndPlansColumn[]
    | ActionCenterColumn[]
    | any;
  data: any;
  pagination?: boolean;
  fixedHeaderScrollHeight?: string;
  perPage: number;
  currentPage: number;
  totalCount: number;
  onPreviousClick?: (e?: any) => void;
  onNextClick?: (e?: any) => void;
  isTableLoading?: boolean;
  GetRow?: any;
  onRowClick?: any;
  "data-testid"?: string;
  ariaLabel?: string;
  getRowTestId?: (row: any, index: number) => string;
  getRowKey?: (row: any, index: number) => React.Key;
}

/* ------------------------------------- TABLE ------------------------------------- */

const CustomTable = (props: CustomTableProps) => {
  const {
    columns,
    data,
    pagination = true,
    fixedHeaderScrollHeight,
    perPage,
    totalCount,
    currentPage,
    onPreviousClick = () => {},
    onNextClick = () => {},
    isTableLoading = false,
    GetRow = () => {},
    onRowClick = () => {},
    "data-testid": dataTestId,
    ariaLabel,
    getRowTestId,
    getRowKey,
  } = props;

  const [viewportWidth, setViewportWidth] = useState<number>(window.innerWidth);

  useEffect(() => {
    const onResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const tableTopRef = useRef<HTMLDivElement>(null);
  const tableWrapperRef = useRef<HTMLDivElement>(null);
  const mobileWrapperRef = useRef<HTMLDivElement>(null);

  // Helper to find the nearest scrollable parent
  function getScrollableParent(node: any) {
    if (!node) return null;
    let parent = node.parentElement;
    while (parent) {
      const style = window.getComputedStyle(parent);
      const overflowY = style.overflowY;
      if (overflowY === "auto" || overflowY === "scroll") {
        return parent;
      }
      parent = parent.parentElement;
    }
    return window;
  }

  const scrollToElement = () => {
    // Mobile: scroll to top of the list as before
    if (viewportWidth < 769) {
      if (tableTopRef.current) {
        const scrollableParent = getScrollableParent(tableTopRef.current);
        const rect = tableTopRef.current.getBoundingClientRect();
        if (scrollableParent === window) {
          const scrollTop =
            window.pageYOffset || document.documentElement.scrollTop;
          window.scrollTo({
            top: rect.top + scrollTop - 70,
            behavior: "smooth",
          });
        } else {
          // Calculate offset relative to the scrollable parent
          const parentRect = scrollableParent.getBoundingClientRect();
          scrollableParent.scrollTo({
            top: scrollableParent.scrollTop + rect.top - parentRect.top - 70,
            behavior: "smooth",
          });
        }
      }
      return;
    }

    // Desktop: scroll to the first data row in the table
    if (tableWrapperRef.current) {
      const table = tableWrapperRef.current.querySelector(".dc-dial-table");
      if (table) {
        table.scrollTo({
          top: 0,
          behavior: "smooth",
        });
        return;
      }
    }
  };

  // Scroll to top of list on pagination (mobile and desktop, only on page change)
  const isFirstPage = useRef(true);
  useEffect(() => {
    if (isTableLoading) return;
    if (isFirstPage.current) {
      isFirstPage.current = false;
      return;
    }
    scrollToElement();
  }, [currentPage, isTableLoading]);

  // Desktop: size the table's scrolling body so the whole page, paginator and
  // its bottom divider included, ends exactly at the bottom of the page area,
  // never past it (which made the page itself scroll) and never short of it.
  // Callers pass a fixed "viewport minus N px" estimate; that's only the first
  // paint. N can't be right everywhere, because what sits above the table
  // changes height with the window width, the app zoom and the debt-collector
  // notice, so the real figure is measured and re-measured whenever any of
  // those change.
  const [fittedHeight, setFittedHeight] = useState<number | null>(null);
  const scrollHeight =
    fittedHeight !== null ? `${fittedHeight}px` : fixedHeaderScrollHeight;

  useLayoutEffect(() => {
    const wrapper = tableWrapperRef.current;
    const pageArea = wrapper?.closest<HTMLElement>(
      '[data-testid$="main-content"]'
    );
    if (!wrapper || !pageArea) return;

    // Column header plus roughly one row. Kept small on purpose: some pages
    // clip overflow instead of scrolling, so a floor taller than the space
    // actually left would hide the paginator rather than scroll to it.
    const MIN_BODY_HEIGHT = 120;

    const fit = () => {
      // Below lg this table isn't rendered (the card list is), and the page is
      // meant to scroll.
      if (window.innerWidth < 1024) return;
      const body = wrapper.querySelector<HTMLElement>(".dc-dial-table");
      if (!body || !pageArea.offsetHeight) return;

      // Rects are in screen pixels, heights we set are CSS pixels; the shell's
      // zoom (see --app-zoom) is the ratio between them.
      const areaRect = pageArea.getBoundingClientRect();
      const scale = areaRect.height / pageArea.offsetHeight || 1;
      const areaStyle = getComputedStyle(pageArea);
      const usableBottom =
        areaRect.bottom -
        (parseFloat(areaStyle.paddingBottom) +
          parseFloat(areaStyle.borderBottomWidth)) *
          scale;
      // Measure the table itself (paginator included), not the page root:
      // some pages clamp their root to the page area with overflow hidden, so
      // the root always "fits" while the table underneath it is clipped.
      const tableBottom =
        wrapper.getBoundingClientRect().bottom + pageArea.scrollTop * scale;

      const delta = (usableBottom - tableBottom) / scale;
      const next = Math.max(
        MIN_BODY_HEIGHT,
        Math.floor(body.offsetHeight + delta)
      );
      setFittedHeight((prev) =>
        prev !== null && Math.abs(prev - next) < 1 ? prev : next
      );
    };

    fit();
    // Refit when the page area changes size (window resize, zoom, the notice)
    // and when anything above the table changes height (filters or cards
    // wrapping, content loading in). Those move the table without resizing
    // it, so watch every ancestor between the table and the page area along
    // with each one's earlier siblings.
    const observer = new ResizeObserver(() => fit());
    observer.observe(pageArea);
    for (
      let node: HTMLElement | null = wrapper;
      node && node !== pageArea;
      node = node.parentElement
    ) {
      let sibling = node.previousElementSibling;
      while (sibling) {
        observer.observe(sibling);
        sibling = sibling.previousElementSibling;
      }
    }
    return () => observer.disconnect();
  }, []);

  const skeletonWidth = Math.max(0, viewportWidth - 40);
  const iconSize = viewportWidth < 500 ? 80 : 100;

  const PaginationComponent = () => {
    const safePerPage = perPage || 0;
    let count: number = totalCount && perPage ? totalCount / perPage : 0;
    let totalPages = Math.ceil(count) || 0;

    const startIndex =
      !totalCount || !safePerPage ? 0 : (currentPage - 1) * safePerPage + 1;
    const endIndex =
      !totalCount || !safePerPage
        ? 0
        : Math.min(totalCount, currentPage * safePerPage);

    return (
      // On desktop the paginator stays put at the bottom of the table area
      // instead of scrolling away with the rows. It needs its own background
      // and a stacking order so rows can't show through as they pass beneath.
      <div className="py-5 flex justify-between lg:px-0 px-5 items-center lg:border-y border-t  border-strokePrimary lg:mb-0 mb-5 lg:sticky lg:bottom-0 lg:z-[2] lg:bg-bgPrimary">
        <div className="text-sm font-normal leading-5 text-txtPrimary">{`Viewing ${startIndex}-${endIndex} of ${totalCount} results`}</div>
        <div className="flex items-center gap-2">
          {/* Both controls stay visible at all times so the pagination row
              doesn't shift as you page through; they're disabled at the ends
              of the range instead of being removed. */}
          <Button
            isDisabled={isTableLoading || currentPage <= 1}
            variant="stroke-outline"
            text="Previous"
            className="!py-1 !px-2 !text-sm !leading-normal"
            onClick={() => {
              onPreviousClick();
            }}
          />
          <Button
            isDisabled={
              isTableLoading || totalPages === 0 || currentPage >= totalPages
            }
            variant="stroke-outline"
            text="Next"
            className="!py-1 !px-2 !text-sm !leading-normal"
            onClick={() => {
              onNextClick();
            }}
          />
        </div>
      </div>
    );
  };

  return (
    <>
      <div ref={tableTopRef}></div>
      <div
        className="lg:block hidden"
        ref={tableWrapperRef}
        data-testid={dataTestId}
        aria-label={ariaLabel}
      >
        <DataTable
          customStyles={{
            table: { style: { minHeight: scrollHeight } },
            headRow: {
              style: {
                color: "#5F6583",
                fontSize: "16px",
                fontWeight: "500",
              },
            },

            noData: {
              style: {
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: scrollHeight,
              },
            },
          }}
          columns={columns}
          data={isTableLoading ? [1, 2, 3, 4, 5, 6, 7, 8, 9, 10] : data}
          className="dc-dial-table animate__animated animate__fadeIn"
          pagination={pagination}
          paginationComponent={PaginationComponent}
          fixedHeader
          noHeader
          fixedHeaderScrollHeight={scrollHeight}
          paginationPerPage={perPage}
          noDataComponent={
            <NoDataComponent
              isTableLoading={isTableLoading}
              iconSize={iconSize}
            />
          }
          onRowClicked={(row: any, e: any) => {
            if (!isTableLoading) {
              onRowClick(row, e);
            }
          }}
        />
      </div>
      {/* No height cap or scroll of its own below lg. Capping this list turned it
          into a scroller nested inside the already-scrolling page, so on a phone
          the records got squeezed into a short window showing roughly one card
          at a time. The designs scroll the mobile page as a single piece, so the
          cards flow and the page shell does the scrolling. */}
      <div
        ref={mobileWrapperRef}
        className="lg:hidden w-full"
        data-testid={dataTestId ? `${dataTestId}-mobile` : undefined}
        aria-label={ariaLabel ? `${ariaLabel} mobile list` : undefined}
      >
        {isTableLoading ? (
          <>
            <div className="w-full h-[1px] bg-strokePrimary"></div>
            {Array.from({ length: 10 }).map((item: any, index: number) => (
              <div
                className="flex flex-col gap-[10px] px-5 w-full py-3"
                key={index}
              >
                <div className="w-full flex items-center justify-center">
                  <Skeleton height={27} width={skeletonWidth} />
                </div>
                <div className="w-[75%] flex flex-col gap-[10px]">
                  {[1, 2, 3].map((i) => (
                    <div
                      className="w-full flex justify-between items-center"
                      key={i}
                    >
                      <div className="w-[65%]">
                        <Skeleton height={20} />
                      </div>
                    </div>
                  ))}
                </div>
                <div className="w-full h-[1px] bg-strokePrimary"></div>
              </div>
            ))}
          </>
        ) : data && data?.length > 0 ? (
          data.map((row: any, index: number) => (
            <div
              key={getRowKey ? getRowKey(row, index) : row?.id || index}
              className="w-full"
              data-testid={getRowTestId?.(row, index)}
            >
              <div className="w-full h-[1px] bg-strokePrimary"></div>
              <div className=" lg:px-0 px-5">{GetRow(row)}</div>
            </div>
          ))
        ) : (
          <div className="flex items-center justify-center min-h-[230px]">
            <NoDataComponent isTableLoading={false} iconSize={iconSize} />
          </div>
        )}
        {totalCount > 0 ? (
          <div className="bg-bgPrimary w-full">
            <PaginationComponent />
          </div>
        ) : null}
      </div>
    </>
  );
};

const NoDataComponent = ({
  isTableLoading,
  iconSize,
}: {
  isTableLoading: boolean;
  iconSize: number;
}) => {
  return isTableLoading ? (
    <></>
  ) : (
    <div className="flex flex-col items-center gap-5 md:gap-7 text-[20px] md:text-[24px] font-medium text-txtPrimary">
      <NoRecordFoundIcon width={iconSize} height={iconSize} /> No records found
    </div>
  );
};

export default CustomTable;
